/**
 * React Query hooks for the billing endpoints (M3.3).
 */
import { useAuth } from "@clerk/clerk-react";
import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  ApiError,
  BudgetResponse,
  BudgetUsageResponse,
  UpdateBudgetRequest,
  SubscriptionResponse,
  CreateCheckoutSessionRequest,
  CreateTopUpCheckoutRequest,
  CreateTopUpCheckoutResponse,
  BillingPortalResponse,
  UpdateAutoRefillRequest,
  WalletResponse,
} from "@/lib/api-client";
import {
  fetchBudget,
  fetchBudgetUsage,
  updateBudget,
  fetchSubscription,
  createCheckoutSession,
  createBillingPortalSession,
  fetchWallet,
  createTopUpCheckout,
  updateAutoRefill,
} from "@/lib/api-client";

export const billingKeys = {
  all: ["billing"] as const,
  budget: () => ["billing", "budget"] as const,
  usage: () => ["billing", "usage"] as const,
  subscription: () => ["billing", "subscription"] as const,
  wallet: () => ["billing", "wallet"] as const,
} as const;

// ─── Budget config ────────────────────────────────────────────────────────────

export type UseBudgetResult = UseQueryResult<BudgetResponse, ApiError | Error>;

export function useBudget(): UseBudgetResult {
  const { getToken } = useAuth();
  return useQuery<BudgetResponse, ApiError | Error>({
    queryKey: billingKeys.budget(),
    queryFn: () => fetchBudget({ getToken: () => getToken() }),
  });
}

// ─── Usage ───────────────────────────────────────────────────────────────────

export type UseBudgetUsageResult = UseQueryResult<BudgetUsageResponse, ApiError | Error>;

export function useBudgetUsage(): UseBudgetUsageResult {
  const { getToken } = useAuth();
  return useQuery<BudgetUsageResponse, ApiError | Error>({
    queryKey: billingKeys.usage(),
    queryFn: () => fetchBudgetUsage({ getToken: () => getToken() }),
    refetchInterval: 60_000,
  });
}

// ─── Update ───────────────────────────────────────────────────────────────────

export type UseUpdateBudgetResult = UseMutationResult<
  BudgetResponse,
  ApiError | Error,
  UpdateBudgetRequest
>;

export function useUpdateBudget(): UseUpdateBudgetResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<BudgetResponse, ApiError | Error, UpdateBudgetRequest>({
    mutationFn: (body) => updateBudget(body, { getToken: () => getToken() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: billingKeys.budget() });
      void qc.invalidateQueries({ queryKey: billingKeys.usage() });
    },
  });
}

// ─── Stripe subscription (M1.1) ──────────────────────────────────────────────

export function useSubscription(): UseQueryResult<SubscriptionResponse, ApiError | Error> {
  const { getToken } = useAuth();
  return useQuery<SubscriptionResponse, ApiError | Error>({
    queryKey: billingKeys.subscription(),
    queryFn: () => fetchSubscription({ getToken: () => getToken() }),
  });
}

export function useCreateCheckout(): UseMutationResult<
  { url: string },
  ApiError | Error,
  CreateCheckoutSessionRequest
> {
  const { getToken } = useAuth();
  return useMutation({
    mutationFn: (vars) => createCheckoutSession(vars, { getToken: () => getToken() }),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });
}

export function useBillingPortal(): UseMutationResult<BillingPortalResponse, ApiError | Error, void> {
  const { getToken } = useAuth();
  return useMutation({
    mutationFn: () =>
      createBillingPortalSession(
        { return_url: `${window.location.origin}/settings/billing` },
        { getToken: () => getToken() },
      ),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });
}

// ─── Credits — wallet + top-ups + auto-refill (M1.5 / M1.6) ──────────────────

export function useWallet(): UseQueryResult<WalletResponse, ApiError | Error> {
  const { getToken } = useAuth();
  return useQuery<WalletResponse, ApiError | Error>({
    queryKey: billingKeys.wallet(),
    queryFn: () => fetchWallet({ getToken: () => getToken() }),
    refetchInterval: 60_000,
  });
}

export function useCreateTopUp(): UseMutationResult<
  CreateTopUpCheckoutResponse,
  ApiError | Error,
  CreateTopUpCheckoutRequest
> {
  const { getToken } = useAuth();
  return useMutation({
    mutationFn: (vars) => createTopUpCheckout(vars, { getToken: () => getToken() }),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });
}

export function useUpdateAutoRefill(): UseMutationResult<
  WalletResponse,
  ApiError | Error,
  UpdateAutoRefillRequest
> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => updateAutoRefill(body, { getToken: () => getToken() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: billingKeys.wallet() });
    },
  });
}
